import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  inject
} from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropListGroup,
  CdkDropList,
  CdkDrag,
  CdkDragPreview,
  CdkDragPlaceholder,
  CdkDragHandle
} from '@angular/cdk/drag-drop';
import { FileSelectorAttachment } from '../file-selector/file-selector.component';
import { catchError, switchMap, of } from 'rxjs';
import {
  DocumentModificationService,
  MergeResponse
} from '../../services/document-modification/document-modification.service';
import { Document } from '../../models/document';
import { MessageService } from 'primeng/api';
import { NgClass } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Checkbox } from 'primeng/checkbox';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Select, SelectChangeEvent } from 'primeng/select';
import {
  Area,
  ContentApiService,
  DocumentType
} from '../../services/content-api/content-api.service';
import { DocumentManagementService } from '../../services/document-management/document-management.service';

export type BulkActionType = 'merge' | 'update';

interface BulkActionLabels {
  headerLabelKey: string;
  emptyMessageKey: string;
  dialogMessageKey: string;
  confirmMessageKey: string;
  pageHeaderKey: string;
  helperMessageKey: string;
}

const BULK_ACTION_LABELS: Record<BulkActionType, BulkActionLabels> = {
  merge: {
    headerLabelKey: 'mergeDocuments',
    emptyMessageKey: 'emptyMergeMessage',
    dialogMessageKey: 'mergeDialogMessage',
    confirmMessageKey: 'confirmMergeMessage',
    pageHeaderKey: 'mergeDocuments',
    helperMessageKey: 'mergeHelpText'
  },
  update: {
    headerLabelKey: 'updateDocuments',
    emptyMessageKey: 'emptyUpdateMessage',
    dialogMessageKey: 'updateDialogMessage',
    confirmMessageKey: 'confirmUpdateMessage',
    pageHeaderKey: 'updateDocumentsHeader',
    helperMessageKey: 'updateDocumentsHelpText'
  }
};

@Component({
  selector: 'app-bulk-document-actions',
  templateUrl: './bulk-document-actions.component.html',
  styleUrls: ['./bulk-document-actions.component.scss'],
  imports: [
    CdkDropListGroup,
    NgClass,
    Bind,
    Checkbox,
    FormsModule,
    ReactiveFormsModule,
    Tooltip,
    Button,
    CdkDropList,
    CdkDrag,
    CdkDragPreview,
    Card,
    CdkDragPlaceholder,
    CdkDragHandle,
    Dialog,
    ProgressSpinner,
    TranslatePipe,
    Select
  ]
})
export class BulkDocumentActionsComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private documentModificationService = inject(DocumentModificationService);
  private messageService = inject(MessageService);
  private contentApiService = inject(ContentApiService);
  private translateService = inject(TranslateService);
  private docMngmtService = inject(DocumentManagementService);

  @Input() attachments: FileSelectorAttachment[] = [];
  @Input() currentAttachmentIndex: number = 0;
  @Input() isLoadingFiles: boolean = false;
  @Input() batch: any = {};
  @Input() isPrinter: boolean = false;
  @Input() isOutlook: boolean = false;
  @Input() bulkActionType: BulkActionType = 'merge';
  @Input() docAreas: Area[] = [];
  @Output() closeBulkActionEvent = new EventEmitter<void>();
  @Output() mergeEvent = new EventEmitter<{ mergedDocument: Document; idsToRemove: string[] }>();
  @Output() updateKeyFieldsEvent = new EventEmitter<{
    documents: FileSelectorAttachment[];
    area: string;
    documentTypeCode?: string;
    documentTypeName?: string;
  }>();

  selectedFiles: FileSelectorAttachment[] = [];
  isMoveActive: boolean = false;
  activeItemIndex: number | null = null;
  isDialogVisible: boolean = false;
  tenantId: string = '';
  isMergeLoading = false;

  docFormGroup: FormGroup = new FormGroup({
    area: new FormControl(''),
    documentTypeCode: new FormControl('')
  });
  documentTypes: DocumentType[] = [];
  filteredDocumentTypes: DocumentType[] = [];

  private keyFieldLabelsByFileId = new Map<string, { area: string; documentType: string }>();

  @ViewChild('labelRef', { static: false }) labelRef!: ElementRef;

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.tenantId = localStorage.getItem('tenantId') || '';

    this.contentApiService.getDocumentTypes().subscribe(documentTypes => {
      this.documentTypes = documentTypes ?? [];
    });
  }

  /**
   * Returns the saved Area display label for a file, or 'Not Specified' if none is set yet.
   * @param file - The file attachment to look up
   */
  getAreaLabel(file: FileSelectorAttachment): string {
    return (
      this.keyFieldLabelsByFileId.get(file.id)?.area ??
      this.translateService.instant('notSpecified')
    );
  }

  /**
   * Returns the saved Document Type display label for a file, or 'Not Specified' if none is set yet.
   * @param file - The file attachment to look up
   */
  getDocumentTypeLabel(file: FileSelectorAttachment): string {
    return (
      this.keyFieldLabelsByFileId.get(file.id)?.documentType ??
      this.translateService.instant('notSpecified')
    );
  }

  /**
   * Fetches and caches the display-ready Area/Document Type labels for a file, for display on
   * its card in the selected-files list.
   * @param file - The file attachment to load labels for
   */
  private async loadKeyFieldLabels(file: FileSelectorAttachment): Promise<void> {
    if (this.keyFieldLabelsByFileId.has(file.id)) return;

    const notSpecified = this.translateService.instant('notSpecified');

    const metadata = await this.docMngmtService.getDocumentContentMetadata(this.tenantId, file.id);
    if (metadata) {
      const formData = this.docMngmtService.parseMetadataToFormData(metadata);

      this.keyFieldLabelsByFileId.set(file.id, {
        area: formData?.area ?? notSpecified,
        documentType: formData?.documentTypeCode ?? notSpecified
      });
    } else if (file.documentTypeCode) {
      this.contentApiService.getDocumentTypeByCode(file.documentTypeCode).subscribe(docType => {
        //this was pulled from extraction data and can be used to build the proper data
        if (docType) {
          this.keyFieldLabelsByFileId.set(file.id, {
            area: docType.areaCode,
            documentType: docType.code
          });
        } else {
          this.keyFieldLabelsByFileId.set(file.id, {
            area: notSpecified,
            documentType: notSpecified
          });
        }
      });
    } else {
      this.keyFieldLabelsByFileId.set(file.id, {
        area: notSpecified,
        documentType: notSpecified
      });
    }
  }
  /**
   * Whether every attachment is currently selected; setting it selects/deselects all
   */
  get selectAll(): boolean {
    const selectableFiles = this.attachments.filter(file => !this.disableFileFromSelection(file));
    return selectableFiles.length > 0 && this.selectedFiles.length === selectableFiles.length;
  }

  set selectAll(value: boolean) {
    this.selectedFiles = value
      ? this.attachments.filter(file => !this.disableFileFromSelection(file))
      : [];
    this.selectedFiles.forEach(file => this.loadKeyFieldLabels(file));
  }

  /**
   * Translation key for the given label slot, based on the current bulk action mode
   * @param key - The label slot to look up
   */
  getLabelKey(key: keyof BulkActionLabels): string {
    return BULK_ACTION_LABELS[this.bulkActionType][key];
  }

  /**
   * Checks if text content overflows the element's width
   * @param element - The HTML element to check for text overflow
   * @returns True if text is overflowing, false otherwise
   */
  isTextOverflowing(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return element.offsetWidth < element.scrollWidth;
  }

  /**
   * Checks whether a file is currently selected
   * @param file - The file attachment to check
   */
  isSelected(file: FileSelectorAttachment): boolean {
    return this.selectedFiles.some(f => f.id === file.id);
  }

  /**
   * Checks whether a file is not eligible for selection because it has already been filed or has expired
   * @param file - The file attachment to check
   */
  disableFileFromSelection(file: FileSelectorAttachment): boolean {
    return file.completed === true || file.isExpired === true;
  }

  /**
   * Toggles the selection state of a file
   * @param file - The file attachment to toggle
   * @param checked - Whether the file's checkbox is now checked
   */
  toggleFileSelection(file: FileSelectorAttachment, checked: boolean): void {
    if (checked) {
      this.addFile(file);
    } else {
      this.removeFile(file);
    }
  }

  /**
   * Adds a file to the selected files list
   * @param file - The file attachment to add
   */
  addFile(file: FileSelectorAttachment) {
    if (!this.isSelected(file)) {
      this.selectedFiles = [...this.selectedFiles, file];
      this.loadKeyFieldLabels(file);
    }
  }

  /**
   * Removes a file from the selected files list
   * @param file - The file attachment to remove
   */
  removeFile(file: FileSelectorAttachment) {
    this.selectedFiles = this.selectedFiles.filter(f => f.id !== file.id);
  }

  /**
   * Handles drag and drop reordering of files
   * @param event - The CDK drag drop event
   */
  drop(event: CdkDragDrop<FileSelectorAttachment[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.cdr.detectChanges();
    }
  }

  // Keyboard Navigation - Reordering Selected Filed
  /**
   * Handles keyboard navigation for reordering selected files
   * @param event - The keyboard event
   * @param index - The index of the item to move
   */
  moveItem(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'Enter':
        this.toggleMoveMode(event, index);
        break;
      case 'Escape':
      case 'Tab':
        this.deactivateMoveMode(event);
        break;
      case 'ArrowUp':
        this.moveUp(event, index);
        break;
      case 'ArrowDown':
        this.moveDown(event, index);
        break;
    }
  }

  /**
   * Toggles move mode for keyboard navigation
   * @param event - The keyboard event
   * @param index - The index of the item
   */
  private toggleMoveMode(event: KeyboardEvent, index: number): void {
    this.isMoveActive = !this.isMoveActive;
    this.activeItemIndex = this.isMoveActive ? index : null;

    event.preventDefault();

    if (this.isMoveActive) {
      this.focusItem(index);
    }
  }

  /**
   * Deactivates move mode for keyboard navigation
   * @param event - The keyboard event
   */
  private deactivateMoveMode(event: KeyboardEvent): void {
    if (this.isMoveActive) {
      this.isMoveActive = false;
      this.activeItemIndex = null;
      event.preventDefault();
    }
  }

  /**
   * Moves an item up in the list
   * @param event - The keyboard event
   * @param index - The current index of the item
   */
  private moveUp(event: KeyboardEvent, index: number): void {
    if (this.isMoveActive && index > 0) {
      moveItemInArray(this.selectedFiles, index, index - 1);
      this.activeItemIndex = index - 1;
      event.preventDefault();
      this.focusItem(this.activeItemIndex);
    }
  }

  /**
   * Moves an item down in the list
   * @param event - The keyboard event
   * @param index - The current index of the item
   */
  private moveDown(event: KeyboardEvent, index: number): void {
    if (this.isMoveActive && index < this.selectedFiles.length - 1) {
      moveItemInArray(this.selectedFiles, index, index + 1);
      this.activeItemIndex = index + 1;
      event.preventDefault();
      this.focusItem(this.activeItemIndex);
    }
  }

  /**
   * Focuses on a specific item in the list
   * @param index - The index of the item to focus
   */
  private focusItem(index: number): void {
    this.cdr.detectChanges();
    const buttons = Array.from(document.querySelectorAll('.drag-button'));
    if (index >= 0 && index < buttons.length) {
      const pButton = buttons[index] as HTMLElement;
      const nativeButton = pButton.querySelector('button');
      if (nativeButton) {
        nativeButton.focus();
      }
    }
  }

  /**
   * Traps focus within the current item during move mode
   * @param event - The keyboard event
   * @param index - The index of the current item
   */
  trapFocus(event: KeyboardEvent, index: number): void {
    if (this.isMoveActive && event.key === 'Tab') {
      event.preventDefault();

      const button = document.querySelectorAll('.drag-button')[index] as HTMLElement;
      if (button) {
        button.focus();
      }
    }
  }

  /**
   * Closes the merge dialog
   */
  closeBulkAction(): void {
    this.closeBulkActionEvent.emit();
  }

  /**
   * Opens the merge confirmation dialog
   */
  openBulkActionsDialog() {
    this.isDialogVisible = true;
  }

  /**
   * Closes the merge confirmation dialog
   */
  closeBulkActionDialog() {
    this.isDialogVisible = false;
  }

  /**
   * Merges multiple documents into a single PDF
   * @param attachments - Array of file attachments to merge
   */
  mergeDocuments(attachments: FileSelectorAttachment[]) {
    this.isMergeLoading = true;
    this.isDialogVisible = false;

    const ids = attachments.map(element => element.id);
    this.tenantId = localStorage.getItem('tenantId') || '';

    // Generate filename
    const originalName = attachments[0].name;
    const lastDotIndex = originalName.lastIndexOf('.');
    const baseName = lastDotIndex !== -1 ? originalName.substring(0, lastDotIndex) : originalName;
    const newName = `${baseName}[merged].pdf`;

    const request = { documentIds: ids, outputFileName: newName };

    this.documentModificationService
      .mergeDocuments(this.tenantId, request)
      .pipe(
        switchMap((response: MergeResponse) =>
          this.documentModificationService.getDocumentWithContent(
            this.tenantId,
            response.documentId
          )
        ),
        catchError((error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `${error}`,
            life: 10000
          });
          this.isMergeLoading = false;
          return of(null);
        })
      )
      .subscribe((document: Document | null) => {
        this.isMergeLoading = false;
        if (document) {
          this.mergeEvent.emit({ mergedDocument: document, idsToRemove: ids });
        }
      });
  }

  /**
   * Dispatches the confirmation dialog's confirm action based on the current bulk action mode
   */
  onConfirm(): void {
    if (this.bulkActionType === 'update') {
      this.updateSelectedDocuments();
    } else {
      this.mergeDocuments(this.selectedFiles);
    }
  }

  /**
   * Handles area selection changes by resetting the document type
   * and filtering the preloaded document types down to the newly selected area.
   * @param event - The select change event containing the selected area code.
   */
  onAreaChange(event: SelectChangeEvent): void {
    const selectedAreaCode = event.value;
    this.docFormGroup.get('documentTypeCode')?.setValue('');

    this.filteredDocumentTypes = selectedAreaCode
      ? this.documentTypes.filter(documentType => documentType.areaCode === selectedAreaCode)
      : [];
  }

  /**
   * Handles document type selection changes.
   * @param event - The select change event containing the selected document type code.
   */
  onDocumentTypeChange(event: SelectChangeEvent): void {
    this.docFormGroup.get('documentTypeCode')?.setValue(event.value);
  }

  /**
   * Applies the selected Area/Document Type to the selected documents.
   */
  private updateSelectedDocuments(): void {
    const { area, documentTypeCode } = this.docFormGroup.getRawValue();
    const documentTypeName = this.filteredDocumentTypes.find(
      documentType => documentType.code === documentTypeCode
    )?.name;

    this.updateKeyFieldsEvent.emit({
      documents: this.selectedFiles,
      area,
      documentTypeCode,
      documentTypeName
    });
    this.isDialogVisible = false;
  }
}
